import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../common/Layout/Header';
import UnifiedSidebar from '../common/Layout/Sidebar';
import Footer from '../common/Layout/Footer';
import TitleHeader from '../common/TitleHeader';
import TranslationReady from '../common/TranslationReady/TranslationReady';
import {
  PageTitleProvider,
  usePageTitle,
} from '../../contexts/PageTitleContext';
import useMenuItems from '../common/Layout/useMenuItems';

import Container from 'apollo-react/components/Container';
import CircularProgress from 'apollo-react/components/CircularProgress';

import CommonBreadcrumbs, {
  pageIcons,
  getPageName,
} from '../common/CommonBreadcrumbs';

const LayoutContent = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [size, setSize] = useState([0, 0]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [pageName, setPageName] = useState(getPageName('admin', location));
  const { t, i18n } = useTranslation();
  const { customTitle } = usePageTitle();
  const { menuItems, isLoading: isMenuLoading } = useMenuItems();

  useEffect(() => {
    const [width] = size;
    if (width < 850 && width !== 0) setIsSidebarCollapsed(true);
  }, [size]);

  useEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const savedSidebarState =
      localStorage.getItem('isSidebarCollapsed') === 'true';
    setIsSidebarCollapsed(savedSidebarState);
  }, []);

  const handleSidebarToggle = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
    if (collapsed) {
      localStorage.setItem('isSidebarCollapsed', true);
    } else {
      localStorage.removeItem('isSidebarCollapsed');
    }
  };

  useEffect(() => {
    setPageName(getPageName('admin', location));
  }, [location]);

  // Translate page name
  const translatedPageName = (() => {
    const lowerPageName = pageName.toLowerCase();
    const underscorePageName = lowerPageName.replace(/\s+/g, '_');
    const dashedPageName = lowerPageName.replace(/\s+/g, '-');

    return (
      t(underscorePageName) || t(dashedPageName) || t(lowerPageName) || pageName
    );
  })();

  // Find the route name for the current page
  const routeName = (() => {
    // eslint-disable-next-line no-undef
    const routes = require('../../routesConfig').adminRoutesConfig;
    const pathname = location.pathname;
    const matchedRoute = routes.find((route) => {
      const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(pathname);
    });
    return matchedRoute
      ? matchedRoute.name || matchedRoute.title?.toLowerCase()
      : '';
  })();

  if (isMenuLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <TranslationReady>
      <div>
        <Container
          className="main-container"
          style={{
            marginLeft: isSidebarCollapsed ? '64px' : '320px',
            width: isSidebarCollapsed ? '100%' : '75%',
          }}
        >
          <Header />

          <UnifiedSidebar
            menuItems={menuItems}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={handleSidebarToggle}
          />

          <main className="main-content">
            <CommonBreadcrumbs
              source="admin"
              pathnames={pathnames}
              location={location}
            />

            <TitleHeader
              title={customTitle || translatedPageName}
              icon={pageIcons[routeName] || null}
            />

            <Outlet key={i18n.language} />
          </main>

          <Footer />
        </Container>
      </div>
    </TranslationReady>
  );
};

// Wrap with PageTitleProvider to allow child components to override the page title
const Layout = () => (
  <PageTitleProvider>
    <LayoutContent />
  </PageTitleProvider>
);

export default Layout;
